import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  MessageSquare, 
  Pin, 
  Lock, 
  Unlock, 
  Trash2, 
  Flag, 
  Shield, 
  ShieldOff,
  Search,
  Eye,
  Heart,
  MessageCircle,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForumAdmin = () => {
  const [activeTab, setActiveTab] = useState('discussions');
  const [discussions, setDiscussions] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Ban user dialog
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [banTarget, setBanTarget] = useState({ userId: '', userName: '' });
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState('7'); // days, empty for permanent
  
  // Stats
  const [stats, setStats] = useState({
    total_discussions: 0,
    total_replies: 0,
    banned_users: 0,
    pending_reports: 0
  });

  useEffect(() => {
    fetchDiscussions();
    fetchBannedUsers();
    fetchReports();
    fetchStats();
  }, []);

  const fetchDiscussions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/discussions?limit=100`);
      const data = await res.json();
      if (data.success) {
        setDiscussions(data.discussions || []);
      }
    } catch (error) {
      console.error('Error fetching discussions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBannedUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/admin/banned-users`);
      const data = await res.json();
      if (data.success) {
        setBannedUsers(data.bans || []);
      }
    } catch (error) {
      console.error('Error fetching banned users:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/admin/reports`);
      const data = await res.json();
      if (data.success) {
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/stats`);
      const data = await res.json();
      if (data.success) {
        setStats({
          total_discussions: data.stats?.discussions || 0,
          total_replies: data.stats?.replies || 0,
          banned_users: bannedUsers.length,
          pending_reports: reports.length
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handlePinTopic = async (topicId, isPinned) => {
    try {
      const endpoint = isPinned ? 'unpin' : 'pin';
      await fetch(`${BACKEND_URL}/api/forum/admin/${endpoint}/${topicId}`, {
        method: 'POST'
      });
      toast({ title: isPinned ? 'Topic unpinned' : 'Topic pinned' });
      fetchDiscussions();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update topic', variant: 'destructive' });
    }
  };

  const handleLockTopic = async (topicId, isLocked) => {
    try {
      const endpoint = isLocked ? 'unlock' : 'lock';
      await fetch(`${BACKEND_URL}/api/forum/admin/${endpoint}/${topicId}`, {
        method: 'POST'
      });
      toast({ title: isLocked ? 'Topic unlocked' : 'Topic locked' });
      fetchDiscussions();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update topic', variant: 'destructive' });
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to delete this topic and all its replies?')) {
      return;
    }
    
    try {
      await fetch(`${BACKEND_URL}/api/forum/admin/topic/${topicId}`, {
        method: 'DELETE'
      });
      toast({ title: 'Topic deleted' });
      fetchDiscussions();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete topic', variant: 'destructive' });
    }
  };

  const handleBanUser = async () => {
    if (!banReason.trim()) {
      toast({ title: 'Please provide a reason', variant: 'destructive' });
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/forum/admin/ban-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: banTarget.userId,
          reason: banReason,
          duration_days: banDuration === 'permanent' ? null : parseInt(banDuration)
        })
      });
      
      toast({ 
        title: 'User banned', 
        description: `${banTarget.userName} has been banned ${banDuration === 'permanent' ? 'permanently' : `for ${banDuration} days`}`
      });
      
      setShowBanDialog(false);
      setBanReason('');
      setBanDuration('7');
      fetchBannedUsers();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to ban user', variant: 'destructive' });
    }
  };

  const handleUnbanUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to unban ${userName}?`)) {
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/forum/admin/unban-user/${userId}`, {
        method: 'POST'
      });
      toast({ title: 'User unbanned', description: `${userName} can now post again` });
      fetchBannedUsers();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to unban user', variant: 'destructive' });
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      await fetch(`${BACKEND_URL}/api/forum/admin/reports/${reportId}/resolve?action=${action}`, {
        method: 'POST'
      });
      toast({ title: 'Report resolved' });
      fetchReports();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to resolve report', variant: 'destructive' });
    }
  };

  const openBanDialog = (userId, userName) => {
    setBanTarget({ userId, userName });
    setShowBanDialog(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubjectColor = (subject) => {
    const colors = {
      'Physics': 'bg-blue-100 text-blue-700',
      'Chemistry': 'bg-green-100 text-green-700',
      'Mathematics': 'bg-purple-100 text-purple-700',
      'General': 'bg-gray-100 text-gray-700'
    };
    return colors[subject] || colors['General'];
  };

  const filteredDiscussions = discussions.filter(d =>
    d.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.author?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_discussions}</p>
                <p className="text-sm text-slate-500">Discussions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_replies}</p>
                <p className="text-sm text-slate-500">Total Replies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{bannedUsers.length}</p>
                <p className="text-sm text-slate-500">Banned Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Flag className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{reports.length}</p>
                <p className="text-sm text-slate-500">Pending Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="discussions">
            <MessageSquare className="w-4 h-4 mr-2" />
            Discussions
          </TabsTrigger>
          <TabsTrigger value="banned">
            <Shield className="w-4 h-4 mr-2" />
            Banned Users
          </TabsTrigger>
          <TabsTrigger value="reports">
            <Flag className="w-4 h-4 mr-2" />
            Reports {reports.length > 0 && <Badge className="ml-2 bg-red-500">{reports.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Discussions Tab */}
        <TabsContent value="discussions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Manage Discussions</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search discussions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchDiscussions}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Loading discussions...</div>
              ) : filteredDiscussions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No discussions found</div>
              ) : (
                <div className="space-y-3">
                  {filteredDiscussions.map((discussion) => (
                    <div 
                      key={discussion.id} 
                      className={`p-4 border rounded-lg ${discussion.is_pinned ? 'bg-amber-50 border-amber-200' : ''} ${discussion.is_locked ? 'bg-slate-50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {discussion.is_pinned && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                                <Pin className="w-3 h-3 mr-1" /> Pinned
                              </Badge>
                            )}
                            {discussion.is_locked && (
                              <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                                <Lock className="w-3 h-3 mr-1" /> Locked
                              </Badge>
                            )}
                            <Badge className={getSubjectColor(discussion.subject)}>
                              {discussion.subject}
                            </Badge>
                          </div>
                          <h4 className="font-medium text-slate-900 truncate">{discussion.title}</h4>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" /> {discussion.author?.name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {formatDate(discussion.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" /> {discussion.views}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" /> {discussion.likes}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-3 h-3" /> {discussion.replies}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePinTopic(discussion.id, discussion.is_pinned)}
                            className={discussion.is_pinned ? 'text-amber-600' : 'text-slate-400'}
                            title={discussion.is_pinned ? 'Unpin' : 'Pin'}
                          >
                            <Pin className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLockTopic(discussion.id, discussion.is_locked)}
                            className={discussion.is_locked ? 'text-slate-600' : 'text-slate-400'}
                            title={discussion.is_locked ? 'Unlock' : 'Lock'}
                          >
                            {discussion.is_locked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openBanDialog(discussion.author?.id, discussion.author?.name)}
                            className="text-slate-400 hover:text-red-600"
                            title="Ban Author"
                          >
                            <Shield className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTopic(discussion.id)}
                            className="text-slate-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banned Users Tab */}
        <TabsContent value="banned" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Banned Users</CardTitle>
              <CardDescription>
                Users who are currently banned from posting in the forum
              </CardDescription>
            </CardHeader>
            <CardContent>
              {bannedUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No banned users</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bannedUsers.map((ban) => (
                    <div key={ban.id} className="p-4 border rounded-lg bg-red-50 border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{ban.user_name || ban.user_id}</span>
                            {ban.is_permanent ? (
                              <Badge className="bg-red-600">Permanent</Badge>
                            ) : (
                              <Badge className="bg-amber-500">Temporary</Badge>
                            )}
                          </div>
                          {ban.user_email && (
                            <p className="text-sm text-slate-500">{ban.user_email}</p>
                          )}
                          <p className="text-sm text-red-600 mt-1">
                            <strong>Reason:</strong> {ban.reason}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Banned: {formatDate(ban.banned_at)}</span>
                            {ban.expires_at && (
                              <span>Expires: {formatDate(ban.expires_at)}</span>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnbanUser(ban.user_id, ban.user_name)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <ShieldOff className="w-4 h-4 mr-1" />
                          Unban
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Reported Content</CardTitle>
              <CardDescription>
                Review and take action on reported discussions and replies
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                  <p>No pending reports</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div key={report.id} className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-amber-100 text-amber-700">
                              {report.content_type === 'topic' ? 'Discussion' : 'Reply'}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              Reported {formatDate(report.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700">
                            <strong>Reason:</strong> {report.reason}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            Content ID: {report.content_id}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResolveReport(report.id, 'delete')}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Content
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResolveReport(report.id, 'dismiss')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Ban User Dialog */}
      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Ban User from Forum
            </DialogTitle>
            <DialogDescription>
              Ban <strong>{banTarget.userName}</strong> from posting in the forum
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Ban Duration</label>
              <Select value={banDuration} onValueChange={setBanDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="permanent">Permanent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason for ban</label>
              <Textarea
                placeholder="Explain why this user is being banned..."
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                rows={3}
              />
            </div>
            {banDuration === 'permanent' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Permanent bans should only be used for severe violations
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBanDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBanUser}>
              <Shield className="w-4 h-4 mr-2" />
              Ban User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForumAdmin;
